import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate, useParams } from 'react-router-dom';
import { FirebaseService } from '../services/firebaseService';

interface EclairagePublicSubcategoryProps {
  t: any;
  isRTL: boolean;
}

const SubcategoryPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 4rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1<{ $isRTL: boolean }>`
  font-size: 3rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageDescription = styled.p<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  color: #64748b;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const EnsemblesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EnsembleCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
`;

const EnsembleImage = styled.div`
  height: 200px;
  width: 100%;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const EnsembleContent = styled.div<{ $isRTL: boolean }>`
  padding: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const EnsembleTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const EnsembleDescription = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProductsList = styled.div`
  margin-bottom: 1.5rem;
`;

const ProductsTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProductItem = styled.div<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ViewDetailsButton = styled.button<{ $isRTL: boolean }>`
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    background: #b91c1c;
    transform: translateY(-2px);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.1rem;
  color: #64748b;
`;

interface Ensemble {
  id: string;
  title: string;
  description: string;
  mainImage: string;
  additionalImages: string[];
  products: string[];
  compositionImage: string;
  brochurePDF: string;
  catalogPDF: string;
  technicalSpecs: { [key: string]: string };
  subcategoryId: string;
  createdAt: Date;
  updatedAt: Date;
}

interface Subcategory {
  id: string;
  name: string;
  description: string;
  image: string;
  createdAt: Date;
  updatedAt: Date;
}

const EclairagePublicSubcategory: React.FC<EclairagePublicSubcategoryProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const navigate = useNavigate();
  const { subcategoryId } = useParams<{ subcategoryId: string }>();
  const [ensembles, setEnsembles] = useState<Ensemble[]>([]);
  const [subcategory, setSubcategory] = useState<Subcategory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (subcategoryId) {
      loadData(subcategoryId);
    }
  }, [subcategoryId]);

  const loadData = async (id: string) => {
    try {
      setLoading(true);
      const [subcategoryData, ensemblesData] = await Promise.all([
        FirebaseService.getById('eclairage-public-subcategories', id) as Promise<Subcategory>,
        FirebaseService.getAll('eclairage-public-ensembles')
      ]);
      
      setSubcategory(subcategoryData);
      // Filter ensembles by subcategory
      const filteredEnsembles = ensemblesData.filter((ensemble: Ensemble) => ensemble.subcategoryId === id);
      setEnsembles(filteredEnsembles);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t.home, path: "/" },
    { label: t.catalogTitle, path: "/catalogue" },
    { label: t.publicLightingTitle, path: "/category/eclairage-public" },
    { label: subcategory?.name || t.subcategory, path: `/category/eclairage-public/subcategory/${subcategoryId}` },
  ];

  const handleEnsembleClick = (ensembleId: string) => {
    navigate(`/category/eclairage-public/${ensembleId}`);
  };

  if (loading) {
    return (
      <SubcategoryPage $isRTL={isRTL}>
        <Container>
          <LoadingSpinner>{t.loadingEnsembles}</LoadingSpinner>
        </Container>
      </SubcategoryPage>
    );
  }

  if (!subcategory) {
    return (
      <SubcategoryPage $isRTL={isRTL}>
        <Container>
          <div>{t.subcategoryNotFound}</div>
        </Container>
      </SubcategoryPage>
    );
  }

  return (
    <SubcategoryPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs $isRTL={isRTL}>
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbLink
                $isLast={index === breadcrumbs.length - 1}
                onClick={() => index < breadcrumbs.length - 1 && navigate(breadcrumb.path)}
              >
                {breadcrumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && <span>›</span>}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <PageHeader>
          <PageTitle $isRTL={isRTL}>{subcategory.name}</PageTitle>
          <PageDescription $isRTL={isRTL}>
            {subcategory.description}
          </PageDescription>
        </PageHeader>

        <EnsemblesGrid ref={ref}>
          {ensembles.map((ensemble, index) => (
            <EnsembleCard
              key={ensemble.id}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => handleEnsembleClick(ensemble.id)}
            >
              <EnsembleImage>
                <img
                  src={ensemble.mainImage}
                  alt={ensemble.title}
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/products/default-product.jpg';
                  }}
                />
              </EnsembleImage>
              <EnsembleContent $isRTL={isRTL}>
                <EnsembleTitle $isRTL={isRTL}>{ensemble.title}</EnsembleTitle>
                <EnsembleDescription $isRTL={isRTL}>
                  {ensemble.description}
                </EnsembleDescription>
                <ProductsList>
                  <ProductsTitle $isRTL={isRTL}>{t.productsAvailable}</ProductsTitle>
                  {ensemble.products.map((product, productIndex) => (
                    <ProductItem key={productIndex} $isRTL={isRTL}>
                      • {product}
                    </ProductItem>
                  ))}
                </ProductsList>
                <ViewDetailsButton $isRTL={isRTL}>
                  {t.viewDetails}
                </ViewDetailsButton>
              </EnsembleContent>
            </EnsembleCard>
          ))}
        </EnsemblesGrid>
      </Container>
    </SubcategoryPage>
  );
};

export default EclairagePublicSubcategory;
